import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaJsonLd from '@/components/base/SchemaJsonLd';
import { reviews, faqs, allMovies } from '@/mocks/movies';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [formStatus, setFormStatus] = useState('');
  const [formError, setFormError] = useState('');

  const topMovies = allMovies.slice(0, 4);

  useEffect(() => {
    document.title = '关于我们 - 屁屁影院 | 品牌介绍、用户口碑、联系我们';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '了解屁屁影院的诞生故事、品牌理念、用户口碑评价。屁屁影院提供免费高清电影、电视剧、动漫资源，无需注册即可在线观看。欢迎联系我们。');
  }, []);

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 屁屁影院',
      description: '了解屁屁影院的诞生故事、品牌理念、用户口碑评价。',
      url: 'https://pipicinema.com/about',
      isPartOf: {
        '@type': 'WebSite',
        name: '屁屁影院',
        url: 'https://pipicinema.com/',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://pipicinema.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '关于我们',
          item: 'https://pipicinema.com/about',
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '屁屁影院',
      url: 'https://pipicinema.com/',
      description: '屁屁影院是您最佳的在线影视娱乐平台。',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@pipicinema.com',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CN',
        addressLocality: '北京',
        addressRegion: '海淀区',
      },
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honey = formData.get('phone_alt') as string;
    if (honey && honey.trim().length > 0) {
      setFormStatus('提交成功！我们会尽快与您联系。');
      setFormError('');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(e.currentTarget.action, {
        method: 'POST',
        body: formData,
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        // not JSON
      }
      const code = (parsed?.code as string) || '';
      const serverMsg =
        (parsed?.meta as Record<string, string>)?.message ||
        (parsed?.message as string) ||
        (parsed?.meta as Record<string, string>)?.detail ||
        responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('提交成功！我们会尽快与您联系。');
        setFormError('');
        e.currentTarget.reset();
      } else {
        const isSpam =
          serverMsg.toLowerCase().includes('spam') ||
          serverMsg.toLowerCase().includes('form data is spam');
        setFormError(isSpam ? '提交失败，请稍后重试。' : serverMsg || '提交失败，请稍后重试。');
        setFormStatus('');
      }
    } catch (err) {
      setFormError('网络错误，请检查网络后重试。');
      setFormStatus('');
    }
  };

  return (
    <>
      <SchemaJsonLd schemas={schemas} />
      <Navbar />
      <Breadcrumb />

      <main className="px-4 md:px-6 max-w-[1400px] mx-auto pb-16">
        <section className="mb-12 text-center py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
            关于屁屁影院
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            屁屁影院成立于2024年，是国内领先的在线影视资源聚合平台。我们致力于为用户提供海量高清电影、电视剧、动漫、综艺资源，让每一位影视爱好者都能享受即点即看的便捷观影体验。
          </p>
        </section>

        <section className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { icon: 'ri-movie-line', number: '10,000+', label: '电影资源' },
              { icon: 'ri-tv-2-line', number: '5,000+', label: '剧集资源' },
              { icon: 'ri-emotion-happy-line', number: '3,000+', label: '动漫资源' },
              { icon: 'ri-user-3-line', number: '100万+', label: '日活跃用户' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg bg-background-100 p-6 text-center"
              >
                <i className={`${stat.icon} text-3xl text-primary-500 mb-3 block`}></i>
                <div className="text-2xl font-bold text-foreground-900 mb-1">{stat.number}</div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
            我们的优势
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {[
              {
                icon: 'ri-hd-line',
                title: '高清画质',
                desc: '屁屁影院提供1080P高清片源，部分影片支持4K超清画质，让您在家也能享受影院级视觉体验。',
              },
              {
                icon: 'ri-flashlight-line',
                title: '极速更新',
                desc: '每日更新最新影视内容，热门电影上映当天即可观看，追剧零等待，永远走在潮流前沿。',
              },
              {
                icon: 'ri-shield-check-line',
                title: '纯净体验',
                desc: '屁屁影院无需注册、无需登录，打开即看。无强制广告干扰，给您最纯粹的观影体验。',
              },
              {
                icon: 'ri-server-line',
                title: '多线路支持',
                desc: '提供多条播放线路，智能选择最优线路，确保播放流畅不卡顿，随时随地畅享高清影视。',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-lg bg-background-50 border border-background-200/70 p-5 md:p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 shrink-0">
                    <i className={`${item.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="reviews" className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="rounded-lg bg-background-50 border border-background-200/70 p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-xs ${
                            i < review.rating ? 'text-accent-500' : 'text-background-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
            热门推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {topMovies.map((movie) => (
              <Link
                key={movie.id}
                to={`/article/${movie.id}`}
                className="group cursor-pointer"
              >
                <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-2">
                  <img
                    src={movie.image}
                    alt={`屁屁影院 - ${movie.title}`}
                    title={`屁屁影院${movie.title}在线观看`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                      {movie.rating}
                    </span>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                  {movie.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>

        <section id="faq" className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
            常见问题
          </h2>
          <div className="rounded-lg border border-background-200/70 overflow-hidden">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`border-b border-background-200/70 last:border-b-0 ${
                  openFaq === index ? 'bg-background-100' : 'bg-background-50'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 text-lg shrink-0 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="mb-12">
          <div className="rounded-xl bg-background-100 p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 mb-6">有任何问题或建议？欢迎与我们联系</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[
                  { icon: 'ri-mail-line', label: '邮箱', value: 'contact@pipicinema.com' },
                  { icon: 'ri-time-line', label: '服务时间', value: '每日 08:00 - 24:00' },
                  { icon: 'ri-map-pin-line', label: '位置', value: '中国 · 北京市海淀区' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-primary-500`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <form
                action="https://readdy.ai/api/form/d9sqsqp4ne17ei0sdmc0"
                method="POST"
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div className="space-y-3">
                  <div>
                    <label htmlFor="name" className="text-sm text-foreground-700 block mb-1">您的姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-sm text-foreground-700 block mb-1">电子邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="text-sm text-foreground-700 block mb-1">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                  </div>
                  {formError && (
                    <p className="text-sm text-primary-500">{formError}</p>
                  )}
                  {formStatus && (
                    <p className="text-sm text-secondary-500">{formStatus}</p>
                  )}
                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-colors cursor-pointer"
                  >
                    发送消息
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}